import { z } from 'zod';
import { cursorPagination, isoDate, latitude, longitude, satang, thaiPhone } from './schemas.js';

/* ========================================================================== */
/* เคสความช่วยเหลือ                                                           */
/* ========================================================================== */

export const CASE_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'approved',
  'rejected',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];
export const caseStatusEnum = z.enum(CASE_STATUSES);

export const CASE_STATUS_LABEL_TH: Record<CaseStatus, string> = {
  draft: 'ฉบับร่าง',
  submitted: 'ยื่นแล้ว',
  under_review: 'กำลังพิจารณา',
  approved: 'อนุมัติแล้ว',
  rejected: 'ไม่อนุมัติ',
  in_progress: 'กำลังช่วยเหลือ',
  completed: 'ปิดเคสแล้ว',
  cancelled: 'ยกเลิก',
};

/**
 * เส้นทางการเปลี่ยนสถานะที่อนุญาต — เก็บไว้ที่เดียวเพื่อให้เซิร์ฟเวอร์เป็นคนตัดสิน
 * ไม่ใช่ปุ่มบนหน้าจอ ถ้าไม่มีตารางนี้จะเกิดเคสที่ "ปิดแล้วกลับมาอนุมัติใหม่ได้"
 */
export const CASE_TRANSITIONS: Record<CaseStatus, readonly CaseStatus[]> = {
  draft: ['submitted', 'cancelled'],
  submitted: ['under_review', 'approved', 'rejected', 'cancelled'],
  under_review: ['approved', 'rejected', 'cancelled'],
  approved: ['in_progress', 'cancelled'],
  rejected: [],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function canTransition(from: CaseStatus, to: CaseStatus): boolean {
  return CASE_TRANSITIONS[from].includes(to);
}

/** สถานะที่ถือว่าเคสจบแล้ว — ใช้กรองรายการค้างและกันแก้ไขย้อนหลัง */
export const CASE_TERMINAL_STATUSES: readonly CaseStatus[] = [
  'rejected',
  'completed',
  'cancelled',
];

export const CASE_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type CasePriority = (typeof CASE_PRIORITIES)[number];
export const casePriorityEnum = z.enum(CASE_PRIORITIES);

export const CASE_PRIORITY_LABEL_TH: Record<CasePriority, string> = {
  low: 'ไม่เร่งด่วน',
  normal: 'ปกติ',
  high: 'เร่งด่วน',
  urgent: 'ฉุกเฉิน',
};

export const REQUEST_CHANNELS = ['line', 'field_visit', 'phone', 'walk_in', 'referral'] as const;
export const requestChannelEnum = z.enum(REQUEST_CHANNELS);

export const REQUEST_CHANNEL_LABEL_TH: Record<(typeof REQUEST_CHANNELS)[number], string> = {
  line: 'LINE',
  field_visit: 'พบตอนลงพื้นที่',
  phone: 'โทรศัพท์',
  walk_in: 'มาที่มูลนิธิ',
  referral: 'หน่วยงานส่งต่อ',
};

export const createCaseSchema = z.object({
  beneficiaryId: z.string().min(1).max(40),
  aidTypeId: z.string().min(1).max(40),
  campaignId: z.string().max(40).optional(),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4000).optional(),
  priority: casePriorityEnum.default('normal'),
  requestChannel: requestChannelEnum.optional(),
  requestedAmount: satang.optional(),
  assignedStaffId: z.string().max(40).optional(),
  dueDate: isoDate.optional(),
  visitId: z.string().max(40).optional(),
});

export const updateCaseSchema = createCaseSchema
  .omit({ beneficiaryId: true, visitId: true })
  .partial();

export const submitCaseSchema = z.object({
  note: z.string().trim().max(2000).optional(),
});

export const approveCaseSchema = z.object({
  /** อนุมัติน้อยกว่าที่ขอได้ — เป็นเรื่องปกติของงานมูลนิธิ */
  approvedAmount: satang,
  note: z.string().trim().max(2000).optional(),
});

export const rejectCaseSchema = z.object({
  reason: z.string().trim().min(5).max(2000),
});

export const changeCaseStatusSchema = z.object({
  status: caseStatusEnum,
  note: z.string().trim().max(2000).optional(),
});

export const listCasesQuerySchema = cursorPagination.extend({
  q: z.string().trim().max(100).optional(),
  status: caseStatusEnum.optional(),
  priority: casePriorityEnum.optional(),
  beneficiaryId: z.string().max(40).optional(),
  aidTypeId: z.string().max(40).optional(),
  assignedStaffId: z.string().max(40).optional(),
  areaId: z.string().max(40).optional(),
  /** เฉพาะเคสที่รออนุมัติในระดับที่ผู้ใช้คนนี้อนุมัติได้ */
  awaitingMyApproval: z.coerce.boolean().optional(),
  overdue: z.coerce.boolean().optional(),
  sort: z.enum(['recent', 'priority', 'due_date']).default('recent'),
});

/* ========================================================================== */
/* การจ่ายความช่วยเหลือ                                                       */
/* ========================================================================== */

export const DISBURSEMENT_METHODS = ['cash', 'transfer', 'goods', 'service', 'voucher'] as const;
export type DisbursementMethod = (typeof DISBURSEMENT_METHODS)[number];
export const disbursementMethodEnum = z.enum(DISBURSEMENT_METHODS);

export const DISBURSEMENT_METHOD_LABEL_TH: Record<DisbursementMethod, string> = {
  cash: 'เงินสด',
  transfer: 'โอนเงิน',
  goods: 'สิ่งของ',
  service: 'บริการ',
  voucher: 'คูปอง/บัตรกำนัล',
};

export const createDisbursementSchema = z
  .object({
    disbursedAt: z.string().datetime({ offset: true }),
    amount: satang.default(0),
    method: disbursementMethodEnum,
    goodsDetail: z.string().trim().max(2000).optional(),
    bankAccountId: z.string().max(40).optional(),
    transferRef: z.string().trim().max(64).optional(),
    visitId: z.string().max(40).optional(),

    receiptKey: z.string().max(255).optional(),
    signatureKey: z.string().max(255).optional(),
    photoKey: z.string().max(255).optional(),
    gpsLat: latitude.optional(),
    gpsLng: longitude.optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .refine((d) => d.amount > 0 || Boolean(d.goodsDetail), {
    message: 'ต้องระบุจำนวนเงินหรือรายละเอียดสิ่งของอย่างน้อยหนึ่งอย่าง',
  })
  .refine((d) => d.method !== 'transfer' || Boolean(d.bankAccountId), {
    message: 'การโอนเงินต้องระบุบัญชีที่โอนออก',
  })
  // เงินสดถึงมือผู้รับต้องมีหลักฐานว่าใครรับ ไม่งั้นตรวจสอบย้อนหลังไม่ได้
  .refine((d) => d.method !== 'cash' || Boolean(d.signatureKey || d.receiptKey), {
    message: 'จ่ายเงินสดต้องแนบลายเซ็นผู้รับหรือใบสำคัญรับเงิน',
  });

export const verifyDisbursementSchema = z.object({
  note: z.string().trim().max(2000).optional(),
});

/* ========================================================================== */
/* คำขอความช่วยเหลือจากประชาชน                                                */
/* ========================================================================== */

export const HELP_REQUEST_STATUSES = [
  'new',
  'screening',
  'accepted',
  'rejected',
  'duplicate',
] as const;
export type HelpRequestStatus = (typeof HELP_REQUEST_STATUSES)[number];
export const helpRequestStatusEnum = z.enum(HELP_REQUEST_STATUSES);

export const HELP_REQUEST_STATUS_LABEL_TH: Record<HelpRequestStatus, string> = {
  new: 'คำขอใหม่',
  screening: 'กำลังคัดกรอง',
  accepted: 'รับเป็นเคสแล้ว',
  rejected: 'ไม่รับเรื่อง',
  duplicate: 'ซ้ำกับคำขอเดิม',
};

export const createHelpRequestSchema = z.object({
  contactName: z.string().trim().min(1).max(200),
  contactPhone: thaiPhone.optional(),
  addressText: z.string().trim().max(500).optional(),
  areaId: z.string().max(40).optional(),
  lat: latitude.optional(),
  lng: longitude.optional(),
  category: z.string().trim().max(64).optional(),
  detail: z.string().trim().min(10).max(4000),
  photoKeys: z.array(z.string().max(255)).max(5).default([]),
  /** ต้องยินยอมให้เก็บข้อมูลก่อน ไม่งั้นรับเรื่องไม่ได้ตาม PDPA */
  consentData: z.literal(true),
});

export const screenHelpRequestSchema = z.object({
  status: z.enum(['screening', 'rejected', 'duplicate']),
  reason: z.string().trim().max(2000).optional(),
  assignedStaffId: z.string().max(40).optional(),
});

/** รับคำขอเป็นเคส — ผูกกับผู้รับความช่วยเหลือเดิม หรือสร้างรายใหม่ */
export const acceptHelpRequestSchema = z.object({
  beneficiaryId: z.string().max(40).optional(),
  createBeneficiary: z
    .object({
      firstName: z.string().trim().min(1).max(100),
      lastName: z.string().trim().min(1).max(100),
      phone: thaiPhone.optional(),
      areaId: z.string().max(40).optional(),
      addressLine: z.string().trim().max(500).optional(),
      primaryOfficerId: z.string().max(40).optional(),
    })
    .optional(),
  openCase: z
    .object({
      aidTypeId: z.string().min(1).max(40),
      title: z.string().trim().min(1).max(200),
      requestedAmount: satang.optional(),
      priority: casePriorityEnum.default('normal'),
    })
    .optional(),
});

export const listHelpRequestsQuerySchema = cursorPagination.extend({
  status: helpRequestStatusEnum.optional(),
  areaId: z.string().max(40).optional(),
  q: z.string().trim().max(100).optional(),
});

/* ========================================================================== */
/* จัดสรรเงินบริจาคไปยังเคส                                                   */
/* ========================================================================== */

export const createAllocationSchema = z
  .object({
    donationId: z.string().min(1).max(40),
    aidCaseId: z.string().max(40).optional(),
    disbursementId: z.string().max(40).optional(),
    amount: satang.refine((v) => v > 0, 'จำนวนเงินต้องมากกว่า 0'),
    note: z.string().trim().max(1000).optional(),
  })
  .refine((d) => Boolean(d.aidCaseId || d.disbursementId), {
    message: 'ต้องระบุเคสหรือรายการจ่ายอย่างน้อยหนึ่งอย่าง',
  });

export const listAllocationsQuerySchema = cursorPagination.extend({
  donationId: z.string().max(40).optional(),
  aidCaseId: z.string().max(40).optional(),
});

/* ========================================================================== */
/* รายงาน                                                                     */
/* ========================================================================== */

export const REPORT_KINDS = [
  'donations',
  'cases',
  'disbursements',
  'beneficiaries',
  'visits',
] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];
export const reportKindEnum = z.enum(REPORT_KINDS);

export const REPORT_KIND_LABEL_TH: Record<ReportKind, string> = {
  donations: 'เงินบริจาค',
  cases: 'เคสความช่วยเหลือ',
  disbursements: 'การจ่ายความช่วยเหลือ',
  beneficiaries: 'ผู้ได้รับความช่วยเหลือ',
  visits: 'การลงพื้นที่',
};

export const reportRangeSchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  campaignId: z.string().max(40).optional(),
  areaId: z.string().max(40).optional(),
  teamId: z.string().max(40).optional(),
});

export const exportQuerySchema = reportRangeSchema.extend({
  kind: reportKindEnum,
  /** ผลรวมสูงสุดที่ยอมให้ดึงในครั้งเดียว — กัน export ทั้งฐานข้อมูลโดยไม่ตั้งใจ */
  limit: z.coerce.number().int().min(1).max(50_000).default(10_000),
});

export const financeFlowQuerySchema = reportRangeSchema.extend({
  groupBy: z.enum(['month', 'campaign', 'aid_type', 'area']).default('month'),
});
